/**
 * Money for stablecoin invoicing (SPEC-IVC, LLD.md section 1).
 *
 * The wire and storage representation is a base-10 string of token BASE UNITS
 * next to an asset `{symbol, decimals}` — never a JSON number, never a float.
 * `{"amount": "1250000000", "asset": {"symbol": "USDC", "decimals": 6}}` is
 * 1,250.000000 USDC.
 *
 * Every arithmetic operation here is `BigInt`, because a token amount can
 * exceed `Number.MAX_SAFE_INTEGER` (a 6-decimal asset overflows it above about
 * 9e9 units, and an 18-decimal asset far sooner in token terms), and because
 * binary floating point cannot represent a decimal fraction exactly: `0.1 + 0.2`
 * is not `0.3`, so a float path silently corrupts a ledger. `src/lib/format-money.ts`
 * is USD-only and parses via `Number`; it is deliberately NOT reused here.
 *
 * Two directions, both lossless and both strict:
 *   - `parseDecimalToBaseUnits` converts what a human typed ("12.50") into base
 *     units for the wire, refusing more fractional digits than the asset has
 *     (`AMOUNT_PRECISION`, LLD section 1).
 *   - `formatBaseUnits` renders base units for display, exactly.
 */

/** The asset an amount is denominated in. Network-independent, per the contract. */
export interface MoneyAsset {
  symbol: string;
  decimals: number;
}

/** The wire shape: a base-unit string plus its asset. Never a JSON number. */
export interface Money {
  amount: string;
  asset: MoneyAsset;
}

/** A refusal code the money helpers raise, drawn from LLD section 12's registry. */
export type MoneyErrorCode = "INVALID_AMOUNT" | "AMOUNT_PRECISION";

/**
 * A money conversion refused a value. `AMOUNT_PRECISION` maps to the contract's
 * 422 code of the same name; `INVALID_AMOUNT` is a malformed string that never
 * reaches the wire (the contract's own `pattern` is what rejects it server-side).
 */
export class MoneyError extends Error {
  readonly code: MoneyErrorCode;

  constructor(code: MoneyErrorCode, message: string) {
    super(message);
    this.name = "MoneyError";
    this.code = code;
  }
}

/**
 * The contract's own amount pattern (OpenAPI `Money.amount`): base-10, no sign,
 * no exponent, no decimal point, no leading zeros except a lone "0", at most 78
 * digits. Exported so callers can validate a wire value before trusting it.
 */
const BASE_UNITS_PATTERN = /^(0|[1-9][0-9]{0,77})$/;

/** True when `value` is a well-formed base-unit string per the contract. */
export function isBaseUnitString(value: unknown): value is string {
  return typeof value === "string" && BASE_UNITS_PATTERN.test(value);
}

/** True when `value` is a valid `decimals` count for an asset (OpenAPI: 0..77). */
export function isAssetDecimals(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value) && value >= 0 && value <= 77;
}

/**
 * Read a base-unit string into a `BigInt`, refusing anything the contract's
 * pattern rejects. Use this instead of `Number(...)` any time an amount will be
 * compared or summed.
 */
export function parseBaseUnits(amount: string): bigint {
  if (!isBaseUnitString(amount)) {
    throw new MoneyError(
      "INVALID_AMOUNT",
      `Not a base-unit amount: expected a base-10 string matching the contract pattern, got "${amount}".`,
    );
  }
  return BigInt(amount);
}

/** As `parseBaseUnits`, but returns `null` instead of throwing (for read paths). */
export function tryParseBaseUnits(amount: string): bigint | null {
  return isBaseUnitString(amount) ? BigInt(amount) : null;
}

function assertDecimals(decimals: number): void {
  if (!isAssetDecimals(decimals)) {
    throw new MoneyError("INVALID_AMOUNT", `Invalid asset decimals: ${String(decimals)} (expected an integer 0..77).`);
  }
}

/** 10^decimals as a BigInt. */
function scale(decimals: number): bigint {
  return 10n ** BigInt(decimals);
}

/**
 * Format base units for display: an exact decimal string with the asset's
 * decimals, no grouping and no rounding. `formatBaseUnits("1250000000", 6)` is
 * `"1250.000000"`; `formatBaseUnits("1", 18)` is `"0.000000000000000001"`;
 * `formatBaseUnits("500", 0)` is `"500"`.
 *
 * Nothing is dropped: if the value does not fit the decimals it is a caller
 * bug (the contract guarantees it does), so this refuses rather than losing a
 * digit. Use `trimMoneyTrailingZeros` for the shorter human form.
 */
export function formatBaseUnits(amount: string | bigint, decimals: number): string {
  assertDecimals(decimals);
  const value = typeof amount === "bigint" ? amount : parseBaseUnits(amount);
  const negative = value < 0n;
  const magnitude = negative ? -value : value;
  const whole = magnitude / scale(decimals);
  const fraction = magnitude % scale(decimals);
  const sign = negative ? "-" : "";
  if (decimals === 0) return `${sign}${whole}`;
  return `${sign}${whole}.${fraction.toString().padStart(decimals, "0")}`;
}

/**
 * The display form: `formatBaseUnits` with trailing fractional zeros removed and
 * thousands separators on the whole part. A whole number keeps its integer form
 * without a trailing dot. Purely presentational — never feed the result back
 * into an API call, use the base-unit string for that.
 */
export function formatBaseUnitsGrouped(amount: string | bigint, decimals: number): string {
  const exact = formatBaseUnits(amount, decimals);
  const [whole, fraction = ""] = exact.split(".");
  const negative = whole.startsWith("-");
  const digits = negative ? whole.slice(1) : whole;
  const grouped = digits.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const trimmedFraction = fraction.replace(/0+$/, "");
  const sign = negative ? "-" : "";
  return trimmedFraction ? `${sign}${grouped}.${trimmedFraction}` : `${sign}${grouped}`;
}

/**
 * Convert what a human typed into base units for the wire (LLD section 1,
 * "Entry from UI"). Accepts an optional sign, a decimal point and thousands
 * separators (`,`), which the user may type: `"1,250.50"` -> `"1250500000"` at 6
 * decimals.
 *
 * Refuses, with `AMOUNT_PRECISION`, a value carrying more fractional digits than
 * the asset has — the server refuses it too and nothing is written, so rounding
 * here would be a silent divergence from the ledger. A negative or malformed
 * value is `INVALID_AMOUNT`; the wire amount is non-negative by contract.
 */
export function parseDecimalToBaseUnits(value: string, decimals: number): string {
  assertDecimals(decimals);
  const trimmed = value.trim().replace(/,/g, "");
  if (trimmed === "") {
    throw new MoneyError("INVALID_AMOUNT", "No amount was entered.");
  }
  const match = /^([+-]?)(\d*)(?:\.(\d*))?$/.exec(trimmed);
  if (!match || (match[2] === "" && (match[3] ?? "") === "")) {
    throw new MoneyError("INVALID_AMOUNT", `Not a decimal amount: "${value}".`);
  }
  const negative = match[1] === "-";
  const whole = match[2] || "0";
  const fraction = match[3] ?? "";
  if (fraction.length > decimals) {
    throw new MoneyError(
      "AMOUNT_PRECISION",
      `This asset has ${decimals} decimal place${decimals === 1 ? "" : "s"}; "${value}" has ${fraction.length}.`,
    );
  }
  const padded = fraction.padEnd(decimals, "0");
  const amount = BigInt(whole) * scale(decimals) + BigInt(padded === "" ? "0" : padded);
  if (negative && amount !== 0n) {
    throw new MoneyError("INVALID_AMOUNT", "An invoice amount cannot be negative.");
  }
  return amount.toString();
}

/** Sum two base-unit amounts. Both must be the same asset — nothing is converted. */
export function addBaseUnits(a: string | bigint, b: string | bigint): bigint {
  return toBigInt(a) + toBigInt(b);
}

/** Subtract `b` from `a`; refuses to go below zero (contract amounts are non-negative). */
export function subtractBaseUnits(a: string | bigint, b: string | bigint): bigint {
  const result = toBigInt(a) - toBigInt(b);
  if (result < 0n) {
    throw new MoneyError("INVALID_AMOUNT", "Subtraction would produce a negative amount.");
  }
  return result;
}

/** Compare two base-unit amounts: -1, 0 or 1. */
export function compareBaseUnits(a: string | bigint, b: string | bigint): -1 | 0 | 1 {
  const left = toBigInt(a);
  const right = toBigInt(b);
  return left < right ? -1 : left > right ? 1 : 0;
}

function toBigInt(value: string | bigint): bigint {
  return typeof value === "bigint" ? value : parseBaseUnits(value);
}

/** Build a wire `Money` from base units. */
export function money(amount: string | bigint, symbol: string, decimals: number): Money {
  assertDecimals(decimals);
  const normalised = typeof amount === "bigint" ? amount.toString() : amount;
  if (!isBaseUnitString(normalised)) {
    throw new MoneyError("INVALID_AMOUNT", `Not a base-unit amount: "${normalised}".`);
  }
  return { amount: normalised, asset: { symbol, decimals } };
}

/** A zero amount in an asset. */
export function zeroMoney(symbol: string, decimals: number): Money {
  return money("0", symbol, decimals);
}

/**
 * Edit value for a form: `Money` -> the human decimal string, grouped, with
 * trailing zeros trimmed. `formatBaseUnitsGrouped` for a `Money`.
 */
export function formatMoney(moneyValue: Money): string {
  return formatBaseUnitsGrouped(moneyValue.amount, moneyValue.asset.decimals);
}

/** True when a wire `Money` is well-formed for its own declared asset. */
export function isMoney(value: unknown): value is Money {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as { amount?: unknown; asset?: unknown };
  if (!isBaseUnitString(candidate.amount)) return false;
  const asset = candidate.asset as { symbol?: unknown; decimals?: unknown } | undefined;
  return typeof asset?.symbol === "string" && isAssetDecimals(asset.decimals);
}
